import React from 'react';
import { MODAL_TYPES, ModalType, ModalComponentProps } from './constants';
import { lazyWithRetry } from '../../utils/lazyWithRetry';

export const MODAL_REGISTRY: Record<ModalType, React.ComponentType<ModalComponentProps>> = {
  [MODAL_TYPES.PATIENT_COMMUNICATION_ONBOARDING]: lazyWithRetry(
    () => import('./PatientCommunicationOnboarding'),
  ),
  [MODAL_TYPES.NEW_FEATURE]: lazyWithRetry(() => import('./NewFeature')),
  [MODAL_TYPES.VERSION_UPDATE]: lazyWithRetry(
    () => import('./VersionUpdateModal'),
  ) as React.ComponentType<ModalComponentProps>,
  [MODAL_TYPES.ENABLE_EHR_MODE]: lazyWithRetry(() => import('./EnableEhrModeModal')),
  [MODAL_TYPES.NEW_PATIENT]: lazyWithRetry(() => import('./NewPatientModal')),
  [MODAL_TYPES.PATIENT_INFO_COLLECTION]: lazyWithRetry(() =>
    import('./PatientInfoCollectionModal').then(m => ({ default: m.PatientInfoCollectionModal })),
  ),
  [MODAL_TYPES.STRIPE_CONNECT_INFO]: lazyWithRetry(() =>
    import('@features/payments/modals').then(m => ({ default: m.StripeConnectInfoModal })),
  ),
  [MODAL_TYPES.PAYMENT_REQUEST]: lazyWithRetry(() =>
    import('@features/payments/modals').then(m => ({ default: m.PaymentRequestModal })),
  ),

  // Toolbar action modals
  [MODAL_TYPES.SEND_COMMUNICATION]: lazyWithRetry(() => import('./toolbar/SendCommunicationModal')),
  [MODAL_TYPES.LAB_ORDER]: lazyWithRetry(() =>
    import('@features/order-labs').then(m => ({ default: m.LabOrderModal })),
  ),
  [MODAL_TYPES.GENERATE_DOCUMENT]: lazyWithRetry(() =>
    import('@features/markdown-editor-ai').then(m => ({ default: m.GenerateDocumentModal })),
  ),
  [MODAL_TYPES.REQUEST_PAYMENT]: lazyWithRetry(() => import('./toolbar/RequestPaymentModal')),

  // Document viewer modals
  [MODAL_TYPES.PDF_VIEWER]: lazyWithRetry(() => import('./PdfViewerModal')),

  [MODAL_TYPES.FIX_SPELLING]: lazyWithRetry(() => import('./FixSpellingModal')),
  [MODAL_TYPES.JOTDOC_FIND_REPLACE]: lazyWithRetry(() => import('./JotDocFindReplaceModal')),

  // Settings modals
  [MODAL_TYPES.COPY_PASTE_PREFERENCES]: lazyWithRetry(() => import('./CopyPastePreferencesModal')),

  // Consent modals
  [MODAL_TYPES.CONSENT_INFORMATION]: lazyWithRetry(() => import('./ConsentInformationModal')),

  // Appointment selection modal
  [MODAL_TYPES.APPOINTMENT_SELECTION]: lazyWithRetry(() =>
    import('@features/appointments/modals').then(m => ({ default: m.AppointmentSelectionModal })),
  ),

  // Appointment types management modal
  [MODAL_TYPES.APPOINTMENT_TYPES_MANAGEMENT]: lazyWithRetry(() =>
    import('@features/appointment-types').then(m => ({
      default: m.AppointmentTypesManagementModal,
    })),
  ),

  // JotCoach feedback modal
  [MODAL_TYPES.JOTCOACH_FEEDBACK]: lazyWithRetry(() =>
    import('@features/jotcoach').then(m => ({ default: m.JotCoachFeedbackModal })),
  ),

  // Approval modal
  [MODAL_TYPES.APPROVAL_MODAL]: lazyWithRetry(() =>
    import('@features/note-ai-edit/components/ApprovalModal').then(m => ({
      default: m.ApprovalModal as any,
    })),
  ) as any,

  // Notepad modal
  [MODAL_TYPES.NOTEPAD]: lazyWithRetry(
    () => import('./NotepadModal'),
  ) as unknown as React.ComponentType<ModalComponentProps>,

  // Medication timeline modal
  [MODAL_TYPES.MEDICATION_TIMELINE]: lazyWithRetry(
    () => import('./MedicationTimelineModal'),
  ) as unknown as React.ComponentType<ModalComponentProps>,

  // CSAT modal
  [MODAL_TYPES.CSAT]: lazyWithRetry(() =>
    import('@features/feedback/components/CSATModal').then(m => ({ default: m.default })),
  ) as unknown as React.ComponentType<ModalComponentProps>,

  // Rule detail modal
  [MODAL_TYPES.RULE_DETAIL]: lazyWithRetry(() => import('./RuleDetailModal')),

  // Rule drilldown modal
  [MODAL_TYPES.RULE_DRILLDOWN]: lazyWithRetry(() => import('./RuleDrilldownModal')) as unknown as React.ComponentType<ModalComponentProps>,

  // Entitlement modals
  [MODAL_TYPES.ENTITLEMENT_CONTACT_ADMIN]: lazyWithRetry(() =>
    import('@features/entitlements/components/ContactAdminUpgradeModal'),
  ) as unknown as React.ComponentType<ModalComponentProps>,

  // Insurance management modal
  [MODAL_TYPES.INSURANCE_EDIT]: lazyWithRetry(() => import('./InsuranceModal')) as unknown as React.ComponentType<ModalComponentProps>,

  // System notification modal
  [MODAL_TYPES.SYSTEM_NOTIFICATION]: lazyWithRetry(() =>
    import('@features/system-notifications').then(m => ({ default: m.SystemNotificationModal })),
  ),

  // JotAudit video modal
  [MODAL_TYPES.JOTAUDIT_VIDEO]: lazyWithRetry(() =>
    import('@features/jotaudit/components/JotAuditVideoModal').then(m => ({ default: m.JotAuditVideoModal })),
  ) as unknown as React.ComponentType<ModalComponentProps>,

  // Entitlement upgrade modal (solo practice admin — add-on or tier upgrade)
  [MODAL_TYPES.ENTITLEMENT_UPGRADE]: lazyWithRetry(() =>
    import('@features/entitlements/components/EntitlementUpgradeModal'),
  ) as unknown as React.ComponentType<ModalComponentProps>,

  // EHR extension promo modal (shown after copy actions)
  [MODAL_TYPES.EHR_EXTENSION_PROMO]: lazyWithRetry(() => import('./EhrExtensionPromoModal')),

  // EHR extension add-on purchase modal (cart-style for practice admins)
  [MODAL_TYPES.EHR_EXTENSION_ADDON]: lazyWithRetry(() =>
    import('@features/integrations/components/EhrExtensionAddonModal'),
  ) as unknown as React.ComponentType<ModalComponentProps>,

  // Billing payment modals
  [MODAL_TYPES.RECORD_PAYMENT]: lazyWithRetry(() =>
    import('@features/billing/components/modals/RecordPaymentModal'),
  ) as unknown as React.ComponentType<ModalComponentProps>,
  [MODAL_TYPES.CAPTURE_CHARGES]: lazyWithRetry(() =>
    import('@features/billing/components/modals/CaptureChargesModal'),
  ) as unknown as React.ComponentType<ModalComponentProps>,
  [MODAL_TYPES.COLLECT_COPAY]: lazyWithRetry(() =>
    import('@features/billing/components/modals/CollectCopayModal'),
  ) as unknown as React.ComponentType<ModalComponentProps>,
};
